import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './Button';
import { ArrowRightIcon } from 'lucide-react';
export function ChoosePathSection() {
  return <section className="pt-16 md:pt-24 pb-0 bg-white" data-id="element-50">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12" data-id="element-51">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="max-w-[680px] mx-auto text-center mb-16" data-id="element-52">
          <div className="flex items-center justify-center gap-4 mb-6" data-id="element-53">
            <div className="w-12 h-[1.5px] bg-gold" data-id="element-54" />
            <span className="font-heading font-bold text-[17px] text-gold uppercase tracking-[0.1em]" data-id="element-55">
              Your Path Forward
            </span>
            <div className="w-12 h-[1.5px] bg-gold" data-id="element-56" />
          </div>
          <h2 className="text-[28px] md:text-[36px] mb-8 tracking-[-0.02em]" data-id="element-57">
            One method. Two ways to use it.
          </h2>
          <p className="text-[16px] leading-[1.75]" data-id="element-58">
            The L.E.A.D.S. Coaching Arch repairs the gap between strategy and
            execution by changing how leaders think, communicate, and move
            people toward action. You can experience it as a leader being
            coached, or you can learn to use it yourself and bring coaching
            capability into your organization.
          </p>
        </motion.div>

        <div className="relative flex items-center justify-center mb-16" data-id="element-59">
          <div className="absolute inset-0 flex items-center" data-id="element-60">
            <div className="w-full border-t-2 border-gold/40" data-id="element-61" />
          </div>
          <div className="relative bg-white px-8" data-id="element-62">
            <span className="font-heading font-bold text-[12px] text-navy uppercase tracking-[0.1em]" data-id="element-63">
              Choose Your Path
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16" data-id="element-64">
          {/* Pathway 1 - Learn to Coach */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.7
        }} className="bg-[#F0EBE3] border-[0.5px] border-gold/20 border-t-2 border-t-gold rounded-xl p-8 md:p-10 flex flex-col" data-id="element-65">
            <div className="flex items-center gap-3 mb-6" data-id="element-66">
              <ArrowRightIcon size={14} className="text-gold" strokeWidth={2.5} data-id="element-67" />
              <span className="font-heading font-bold text-[11px] text-gold uppercase tracking-[0.1em]" data-id="element-68">
                I want to learn to coach
              </span>
            </div>
            <h3 className="text-[24px] md:text-[26px] mb-2 tracking-[-0.01em]" data-id="element-69">
              Leaders Who Coach Academy
            </h3>
            <p className="font-subtitle italic text-[16px] text-charcoal mb-8" data-id="element-70">
              Professional coaching education (ICF-aligned)
            </p>
            <p className="text-[16px] leading-[1.75] mb-8 flex-grow" data-id="element-71">
              Learn the L.E.A.D.S. Coaching Arch, practice it in AI and live
              coaching labs, and develop the ability to close the
              strategy-execution gap inside your own organization by coaching
              the people responsible for carrying it out.
            </p>
            <div className="pt-6 border-t border-warm-border mb-8" data-id="element-72">
              <p className="text-[14px] leading-[1.6]" data-id="element-73">
                <span className="font-medium text-navy" data-id="element-74">Best for:</span> leaders
                who want to develop coaching capability and multiply leadership
                across their teams.
              </p>
            </div>
            <Button variant="secondary" className="w-full" href="https://schallercoaching.com/lwca" data-id="element-75">
              Explore the Academy
            </Button>
          </motion.div>

          {/* Pathway 2 - Be Coached (Premium dark card) */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.7,
          delay: 0.15
        }} className="bg-navy border-t-2 border-t-gold rounded-xl p-8 md:p-10 flex flex-col" data-id="element-76">
            <div className="flex items-center gap-3 mb-6" data-id="element-77">
              <ArrowRightIcon size={14} className="text-gold" strokeWidth={2.5} data-id="element-78" />
              <span className="font-heading font-bold text-[11px] text-gold uppercase tracking-[0.1em]" data-id="element-79">
                I want to be coached
              </span>
            </div>
            <h3 className="text-[24px] md:text-[26px] mb-2 tracking-[-0.01em] text-white" data-id="element-80">
              Leadership Capacity Coaching
            </h3>
            <p className="font-subtitle italic text-[16px] text-warm-light mb-8" data-id="element-81">
              Strategic thinking partnership (1:1 and partners)
            </p>
            <p className="text-[16px] leading-[1.75] mb-8 flex-grow text-warm-light/90" data-id="element-82">
              Work directly with me using the L.E.A.D.S. Coaching Arch to think
              clearly, repair the alignment between your strategy and your
              people, and turn vision into action through others instead of
              carrying it yourself.
            </p>
            <div className="pt-6 border-t border-white/15 mb-8" data-id="element-83">
              <p className="text-[14px] leading-[1.6] text-warm-light/80" data-id="element-84">
                <span className="font-medium text-white" data-id="element-85">Best for:</span>{' '}
                entrepreneurs, founders, and executives navigating real
                leadership pressure right now.
              </p>
            </div>
            <Button variant="white" className="w-full" href="https://schallercoaching.com/lcc" data-id="element-86">
              Discover Leadership Capacity Coaching
            </Button>
          </motion.div>
        </div>
      </div>
    </section>;
}